<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import showMore from '@/assets/icons/blog/show-more.svg'
import type { TTags } from '@/components/PlusBlog/type'

interface TToggleCopy {
    more: string
    less: string
}

const props = defineProps<{
    data: TTags[]
    toggleCopy?: TToggleCopy
}>()

const showAllTags = ref(false)
const toggleCopy = props.toggleCopy ?? {
    more: 'Więcej tagów',
    less: 'Mniej tagów',
}

const visibleTags = ref<boolean[]>([])

const refContainer = ref<HTMLElement | null>(null)

const observers: IntersectionObserver[] = []

const shouldShowToggleButton = computed(
    () => props.data?.length > visibleTags.value.filter(Boolean).length || showAllTags.value,
)

function toggleTagVisibility() {
    showAllTags.value = !showAllTags.value
}

onMounted(() => {
    if (!refContainer.value) {
        return
    }

    const observer = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const index = entry.target.getAttribute('data-index')
                if (index !== null) {
                    visibleTags.value[Number(index)] = entry.isIntersecting
                }
            })
        },
        {
            root: refContainer.value,
            threshold: 0.1,
        },
    )

    refContainer.value.querySelectorAll('[data-index]').forEach((tagElement) => {
        observer.observe(tagElement)
    })

    observers.push(observer)
})

onBeforeUnmount(() => {
    observers.forEach((observer) => observer.disconnect())
})
</script>

<template>
    <div
        ref="refContainer"
        class="page-plus flex flex-wrap gap-2"
        :class="[
            showAllTags ? 'h-auto overflow-visible' : 'ml-[-8px] mt-[-8px] max-h-[94px] overflow-hidden py-2 pl-2',
        ]"
    >
        <a
            v-for="(el, idx) in props.data"
            :key="idx"
            :href="el.url"
            :data-index="idx"
            :tabindex="visibleTags[idx] ? 0 : -1"
            class="bg-plus-blue p-2 text-sm hover:bg-plus-blue-100 dark:bg-plus-primary dark:text-white dark:hover:bg-plus-blue-900 dark:hover:text-white"
        >
            <p v-html="el.name" />
        </a>
    </div>
    <button
        v-if="shouldShowToggleButton"
        class="mt-4 flex cursor-pointer items-center gap-2 text-sm text-plus-blue-700"
        @click="toggleTagVisibility"
    >
        {{ showAllTags ? toggleCopy.less : toggleCopy.more }}
        <AtomImage
            :src="showMore"
            :img-class="[showAllTags ? 'rotate-180' : 'rotate-0']"
        />
    </button>
</template>
